import { Offer } from "@/schemas/firestore/offer.schema";
import { useStore } from "@/store";

export interface OffersResult {
  // Offer State
  offers: Offer[];
  selectedOfferId: string | null;
  isLoading: boolean;
  error: string | null;

  // Offer Actions
  subscribeToOffers: (userId: string, domainId: string) => () => void;
  setSelectedOffer: (offerId: string | null) => void;
  setOfferEnabled: (
    domainId: string,
    offerId: string,
    isEnabled: boolean,
  ) => Promise<void>;
  clearOffers: () => void;
  unsubscribeOffers: () => void;

  // Computed values
  selectedOffer: Offer | null;
}

/**
 * Custom hook to access and manage offer-related data and operations
 * @param domainId - Optional domain ID to filter offers
 */
export const useOffers = (domainId?: string): OffersResult => {
  const {
    // Offer state
    offers: allOffers,
    selectedOfferId,
    isLoading,
    error,

    // Offer actions
    subscribeToOffers,
    setSelectedOffer,
    setOfferEnabled,
    clearOffers,
    unsubscribeOffers,
  } = useStore();

  // Filter offers by domain if domainId is provided
  const offers = domainId
    ? allOffers.filter((o) => o.domain_id === domainId)
    : allOffers;

  // Computed values
  const selectedOffer = offers.find((o) => o.id === selectedOfferId) || null;

  return {
    // Offer state
    offers,
    selectedOfferId,
    isLoading,
    error,

    // Offer actions
    subscribeToOffers,
    setSelectedOffer,
    setOfferEnabled,
    clearOffers,
    unsubscribeOffers,

    // Computed values
    selectedOffer,
  };
};
